import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { onAuthStateChanged, type User } from "firebase/auth";

import { auth, signOutCompletely } from "./firebase";
import { getProfile } from "./api/contractor";
import { ApiError } from "./api/client";
import type { Contractor } from "./types/models";

/**
 * The onboarding funnel, ported verbatim from the mobile router's redirect
 * guard (`app_router.dart:82-139`). A contractor must clear all four gates in
 * order before any part of the workspace is reachable:
 *
 *   signed in → email verified → profile complete → payout account
 *
 * Shipping a login-only web app would strand every contractor mid-onboarding
 * with no route forward, because the app applies exactly these redirects and
 * the backend refuses to quote without a payout account.
 */
export type AuthGate =
  | "loading"
  /** The profile could not be read for a reason that is NOT "no record yet". */
  | "unavailable"
  | "signedOut"
  | "needsEmailVerification"
  /** Terminal: this email's `userType` claim is "homeowner", not "contractor". */
  | "wrongRole"
  | "needsProfile"
  | "needsBankDetails"
  | "ready";

export interface AuthState {
  gate: AuthGate;
  user: User | null;
  contractor: Contractor | null;
  /** Set when the profile fetch failed for a reason other than "no record yet". */
  error: string | null;
}

interface AuthContextValue extends AuthState {
  /** Re-reads /profile and recomputes the gate. Call after completing a step. */
  refresh: () => Promise<void>;
  /** Re-reads the Firebase user, for the email-verification poll. */
  reloadUser: () => Promise<void>;
  signOut: () => Promise<void>;
  /** Lets a screen publish a freshly-fetched profile without a round trip. */
  setContractor: (contractor: Contractor) => void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

/** `isProfileComplete` in `contractor.dart:77`. */
function isProfileComplete(contractor: Contractor): boolean {
  return contractor.skills?.length > 0 && contractor.serviceAreaGeos?.length > 0;
}

function gateFor(
  user: User | null,
  emailVerified: boolean,
  contractor: Contractor | null,
  error: string | null,
  wrongRole: boolean,
): AuthGate {
  if (!user) return "signedOut";
  if (!emailVerified) return "needsEmailVerification";

  // Checked before "unavailable"/"needsProfile": a homeowner's GET /profile
  // 403 is indistinguishable, by status code alone, from a contractor whose
  // token predates /auth/complete (see the note in loadProfile below) — the
  // claim is the only reliable signal, and it is terminal, not a step to route
  // through onboarding.
  if (wrongRole) return "wrongRole";

  // A failed profile read must NOT fall through to "needsProfile". That gate
  // leads to a blank onboarding form, and submitting it calls /auth/complete →
  // CreateContractor → `Doc(id).Set(...)`, a FULL overwrite. An established
  // contractor who hit a cold-start 500 would silently lose their verification,
  // rating, job count, payout account and booking configuration.
  if (error) return "unavailable";

  // With no error, an absent record genuinely means /auth/complete has not run
  // yet — the same place an incomplete profile belongs.
  if (!contractor || !isProfileComplete(contractor)) return "needsProfile";
  if (!contractor.hasPayoutAccount) return "needsBankDetails";
  return "ready";
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  // Tracked separately from `user.emailVerified` on purpose. `user.reload()`
  // mutates the SAME User object in place (Object.assign inside the SDK) and
  // notifies listeners with that identical reference, so React bails out of the
  // re-render and the verification gate never advances — a newly registered
  // contractor would be stranded on /verify-email forever, clicking a link that
  // demonstrably worked.
  const [emailVerified, setEmailVerified] = useState(false);
  const [contractor, setContractorState] = useState<Contractor | null>(null);
  const [resolved, setResolved] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [wrongRole, setWrongRole] = useState(false);

  // Guards against a slow profile fetch for a signed-out-since user landing on
  // top of newer state.
  const requestSeq = useRef(0);

  const loadProfile = useCallback(async (current: User | null) => {
    const seq = ++requestSeq.current;

    if (!current) {
      if (seq === requestSeq.current) {
        setContractorState(null);
        setError(null);
        setWrongRole(false);
        setResolved(true);
      }
      return;
    }

    // An unverified account has no record to fetch and the API would answer 404;
    // skip the call so the verification screen is not gated on a round trip.
    if (!current.emailVerified) {
      if (seq === requestSeq.current) {
        setContractorState(null);
        setError(null);
        setWrongRole(false);
        setResolved(true);
      }
      return;
    }

    try {
      // Read the claim BEFORE calling /profile, and never infer it from the
      // response status. AuthMiddleware answers the SAME 403 for userType == ""
      // (registration incomplete) and userType == "homeowner" (a valid homeowner
      // on the wrong site) — src/shared/pkg/auth/auth.go:189-192 — so the status
      // code cannot tell them apart. The sibling homeowner site hits the mirror
      // image of this and solves it the same way (lib/session.ts's readUserType).
      let claimed = (await current.getIdTokenResult()).claims["userType"];
      if (!claimed) {
        // The token may predate /auth/complete minting the claim — force a
        // refresh once before deciding, same as the sibling's readUserType.
        claimed = (await current.getIdTokenResult(true)).claims["userType"];
      }
      if (claimed === "homeowner") {
        if (seq === requestSeq.current) {
          setContractorState(null);
          setError(null);
          setWrongRole(true);
          setResolved(true);
        }
        return;
      }

      const profile = await getProfile();
      if (seq !== requestSeq.current) return;
      setContractorState(profile);
      setError(null);
      setWrongRole(false);
    } catch (err) {
      if (seq !== requestSeq.current) return;
      // 404 is the ordinary "signed up but /auth/complete hasn't run" state, and
      // 403 is a token that predates the claim — both mean "go finish
      // onboarding", not "something broke".
      const benign = err instanceof ApiError && (err.status === 404 || err.status === 403);
      setContractorState(null);
      setWrongRole(false);
      setError(benign ? null : err instanceof Error ? err.message : "Could not load your profile.");
    } finally {
      if (seq === requestSeq.current) setResolved(true);
    }
  }, []);

  useEffect(() => {
    return onAuthStateChanged(auth(), (next) => {
      setUser(next);
      setEmailVerified(next?.emailVerified ?? false);
      setResolved(false);
      void loadProfile(next);
    });
  }, [loadProfile]);

  const refresh = useCallback(async () => {
    await loadProfile(auth().currentUser);
  }, [loadProfile]);

  const reloadUser = useCallback(async () => {
    const current = auth().currentUser;
    if (!current) return;
    await current.reload();
    // Reading the flag off the (mutated) object into React state is what makes
    // the change visible — see the note on `emailVerified` above. Re-setting
    // `user` alone is a no-op, because it is the very same reference.
    const refreshed = auth().currentUser;
    setEmailVerified(refreshed?.emailVerified ?? false);
    await loadProfile(refreshed);
  }, [loadProfile]);

  const signOut = useCallback(async () => {
    await signOutCompletely();
  }, []);

  const setContractor = useCallback((next: Contractor) => {
    setContractorState(next);
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({
      gate: resolved ? gateFor(user, emailVerified, contractor, error, wrongRole) : "loading",
      user,
      contractor,
      error,
      refresh,
      reloadUser,
      signOut,
      setContractor,
    }),
    [
      resolved,
      user,
      emailVerified,
      contractor,
      error,
      wrongRole,
      refresh,
      reloadUser,
      signOut,
      setContractor,
    ],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (!context) throw new Error("useAuth must be used inside <AuthProvider>");
  return context;
}

/** The signed-in contractor, for screens that render only past the `ready` gate. */
export function useContractor(): Contractor {
  const { contractor } = useAuth();
  if (!contractor) throw new Error("useContractor used outside an authenticated route");
  return contractor;
}
